import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { getTrackBackground, Range } from 'react-range';
import styled from 'styled-components';
import { spacing7x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { isPlainObject } from '../../utils/lang.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["ref"];
var TrackWrapper = styled.div.withConfig({
  displayName: "Slider__TrackWrapper",
  componentId: "sc-1251z5d-0"
})(["&&{padding-top:10px;width:100%;padding-bottom:", ";}"], function (_ref) {
  var tall = _ref.tall;
  return tall ? spacing7x : "12px";
});
var Thumb = styled.div.withConfig({
  displayName: "Slider__Thumb",
  componentId: "sc-1251z5d-1"
})(["&&{outline:none;border-radius:50%;box-shadow:0 1px 2px 0 rgba(0,0,0,0.4);", ";}"], function (props) {
  return "background-color: ".concat(props.theme.CTAColors.primary, ";");
});
var TooltipTrigger = styled.div.withConfig({
  displayName: "Slider__TooltipTrigger",
  componentId: "sc-1251z5d-2"
})(["&&{width:100%;height:100%;position:absolute;}"]);
var Track = styled.div.withConfig({
  displayName: "Slider__Track",
  componentId: "sc-1251z5d-3"
})(["&&{width:100%;align-self:center;height:2px;border-radius:1px;background:", ";opacity:", ";}"], function (props) {
  return getTrackBackground({
    values: props._values,
    colors: [props.theme.CTAColors.primary, props.theme.neutralColors.silver],
    min: props._min,
    max: props._max,
    rtl: props._isRTL
  });
}, function (_ref2) {
  var _disabled = _ref2._disabled;
  return _disabled ? "50%" : null;
});
var Mark = styled.div.withConfig({
  displayName: "Slider__Mark",
  componentId: "sc-1251z5d-4"
})(["&&{height:8px;border-radius:1px;width:2px;display:flex;align-items:center;justify-content:center;background:", ";}"], function (props) {
  return props.theme.neutralColors.rhino;
});
var MarkLabel = styled.span.withConfig({
  displayName: "Slider__MarkLabel",
  componentId: "sc-1251z5d-5"
})(["&&{color:", ";font-size:12px;font-weight:bold;position:absolute;top:16px;white-space:nowrap;user-select:", ";}"], function (props) {
  return props.theme.neutralColors.charcoal;
}, function (_ref3) {
  var _disabled = _ref3._disabled;
  return _disabled ? "none" : void 0;
});

var Slider = function Slider(_ref4) {
  var ariaLabelledBy = _ref4["aria-labelledby"],
      className = _ref4.className,
      disabled = _ref4.disabled,
      marks = _ref4.marks,
      _ref4$max = _ref4.max,
      max = _ref4$max === void 0 ? 100 : _ref4$max,
      _ref4$min = _ref4.min,
      min = _ref4$min === void 0 ? 0 : _ref4$min,
      _ref4$step = _ref4.step,
      step = _ref4$step === void 0 ? 10 : _ref4$step,
      tooltipProps = _ref4.tooltipProps,
      value = _ref4.value,
      _ref4$getValueText = _ref4.getValueText,
      getValueText = _ref4$getValueText === void 0 ? function (val) {
    return "".concat(val);
  } : _ref4$getValueText,
      _onChange = _ref4.onChange,
      onChangeComplete = _ref4.onChangeComplete,
      onValueError = _ref4.onValueError,
      _ref4$renderMarkLabel = _ref4.renderMarkLabel,
      renderMarkLabel = _ref4$renderMarkLabel === void 0 ? function (_ref5) {
    var label = _ref5.label;
    return label;
  } : _ref4$renderMarkLabel,
      _ref4$renderTooltipCo = _ref4.renderTooltipContent,
      renderTooltipContent = _ref4$renderTooltipCo === void 0 ? function (val) {
    return "".concat(val);
  } : _ref4$renderTooltipCo;
  var validated = value === void 0 || value < min ? min : value;
  validated = validated > max ? max : validated;
  var markCount = (max - min) / step;

  var _React$useState = React.useState(validated),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      currValue = _React$useState2[0],
      setCurrValue = _React$useState2[1];

  var _React$useState3 = React.useState(void 0),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      open = _React$useState4[0],
      setOpen = _React$useState4[1];

  var prevValue = React.useRef(validated);

  var _useThemeContext = useThemeContext(),
      isRTL = _useThemeContext.isRTL;

  React.useEffect(function () {
    if (validated !== prevValue.current) {
      setCurrValue(validated);
      prevValue.current = validated;
    }
  }, [validated]);
  React.useEffect(function () {
    if (value === void 0) return;

    if (value < min || value > max) {
      if (process.env.NODE_ENV !== "production") {
        var errorMsg = value < min ? "Invalid incoming value: ".concat(value, " is lesser than ").concat(min, ". 'min' has been used instead.") : "Invalid incoming value: ".concat(value, " is greater than ").concat(max, ". 'max' has been used instead.");
        console.error(errorMsg);
      }

      if (onValueError) onValueError(value || 0, validated);
    }
  }, [max, min, onValueError, value, validated]);
  return /*#__PURE__*/jsx(Range, {
    disabled: disabled,
    max: max,
    min: min,
    step: step,
    rtl: !!isRTL,
    values: [currValue],
    onFinalChange: onChangeComplete ? function (values) {
      return onChangeComplete(values[0]);
    } : void 0,
    onChange: function onChange(values) {
      var nextVal = Math.min(values[0], max);
      setCurrValue(nextVal);
      if (_onChange) _onChange(nextVal);
    },
    renderMark: marks ? function (_ref6) {
      var p = _ref6.props,
          index = _ref6.index;
      var val = (isRTL ? markCount - index : index) * (step || 1) + min;
      var shouldRender = isPlainObject(marks) ? marks[index] : marks;
      shouldRender = val > max ? false : shouldRender;
      if (!shouldRender) return null;
      var label = renderMarkLabel({
        disabled: disabled,
        index: index,
        step: step,
        min: min,
        label: "".concat(val)
      });
      return /*#__PURE__*/jsx(Mark, _objectSpread(_objectSpread({}, p), {}, {
        style: p.style,
        children: label ? /*#__PURE__*/jsx(MarkLabel, {
          _disabled: disabled,
          children: label
        }) : null
      }));
    } : void 0,
    renderThumb: function renderThumb(_ref7) {
      var props = _ref7.props,
          isDragged = _ref7.isDragged;
      var isOpen = isDragged === true ? isDragged : void 0;
      isOpen = open === false ? open : isOpen;
      return /*#__PURE__*/jsx(Thumb, _objectSpread(_objectSpread({
        "aria-labelledby": ariaLabelledBy,
        "aria-valuetext": getValueText(currValue)
      }, props), {}, {
        onKeyDown: function onKeyDown(e) {
          setOpen(false);
          props.onKeyDown(e);
        },
        onKeyUp: function onKeyUp(e) {
          setOpen(void 0);
          props.onKeyUp(e);
        },
        style: _objectSpread(_objectSpread({}, props.style), {}, {
          cursor: disabled ? "not-allowed" : props.style.cursor,
          height: isDragged ? 18 : 12,
          width: isDragged ? 18 : 12
        }),
        children: !disabled && /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({
          content: renderTooltipContent(currValue),
          showing: isOpen
        }, tooltipProps), {}, {
          children: /*#__PURE__*/jsx(TooltipTrigger, {})
        }))
      }));
    },
    renderTrack: function renderTrack(_ref8) {
      var _ref8$props = _ref8.props,
          ref = _ref8$props.ref,
          props = _objectWithoutProperties(_ref8$props, _excluded),
          children = _ref8.children;

      return /*#__PURE__*/jsx(TrackWrapper, _objectSpread(_objectSpread({
        className: className,
        tall: !!marks
      }, props), {}, {
        children: /*#__PURE__*/jsx(Track, {
          _disabled: disabled,
          _values: [currValue],
          _min: min,
          _max: max,
          _isRTL: !!isRTL,
          ref: ref,
          children: children
        })
      }));
    }
  });
};

Slider.propTypes = {
  "aria-labelledby": _PropTypes.string,
  "className": _PropTypes.string,
  "disabled": _PropTypes.bool,
  "getValueText": _PropTypes.func,
  "marks": _PropTypes.oneOfType([_PropTypes.object, _PropTypes.bool]),
  "max": _PropTypes.number,
  "min": _PropTypes.number,
  "onChange": _PropTypes.func,
  "onChangeComplete": _PropTypes.func,
  "onValueError": _PropTypes.func,
  "renderMarkLabel": _PropTypes.func,
  "renderTooltipContent": _PropTypes.func,
  "step": _PropTypes.number,
  "tooltipProps": _PropTypes.object,
  "value": _PropTypes.number
};
Slider.displayName = "Slider";

export { Slider };
